import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import ActionButtonSmall from "../../components/layout/buttons/action-button-small";
import Colors from "../../enums/colors";
import { useHistory, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { Country } from "../../models/country/country";
import { LanguagesContext } from "../../context/languages";
import { TimezonesContext } from "../../context/timezones";
import { CountriesContext } from "../../context/countries";
import API from "../../api/api";
import ApiUri from "../../api/api-uri";
import { Language } from "../../models/language/language";
import BaseComponentLogin from "./baseComponentLogin";
import { isValidEmail } from ".";
import ReCAPTCHA from "react-google-recaptcha";
import { getCookie, setCookie } from "../../App";
import { AuthContext } from "../../context/auth";
import { TranslationsContext } from "../../context/translations";
import { getPasswordValidationTexts } from "../account/PasswordSettings";
import { useParams } from "react-router";
import { InformationContext } from "../../context/information";

const stripDiacritics = (str: string) =>
  str.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Some browsers return the ISO-updated name (e.g. "Türkiye") while the API still has the old name.
// Keys are the stripped (no-diacritic, lowercase) form of what Intl.DisplayNames returns.
// Values are the lowercase country name as stored in the API.
const COUNTRY_NAME_ALIASES: Record<string, string> = {
  "turkiye": "turkey",
};

const matchCountryName = (list: any[], countryName: string) => {
  const exact = list.find((c) => c.country?.toLowerCase() === countryName.toLowerCase());
  if (exact) return exact;

  const aliased = COUNTRY_NAME_ALIASES[stripDiacritics(countryName)];
  if (aliased) {
    const byAlias = list.find((c) => c.country?.toLowerCase() === aliased);
    if (byAlias) return byAlias;
  }

  const partials = list.filter(
    (c) =>
      c.country?.toLowerCase().includes(countryName.toLowerCase()) ||
      countryName.toLowerCase().includes(c.country?.toLowerCase())
  );
  if (partials.length > 0) {
    partials.sort(
      (a, b) =>
        Math.abs(a.country.length - countryName.length) -
        Math.abs(b.country.length - countryName.length)
    );
    return partials[0];
  }
  return undefined;
};

// Static IANA timezone → ISO 3166-1 alpha-2 country code map.
// Used as the primary country detection method — works in all browsers without
// relying on Intl.Locale.getTimeZones() which is only available in newer engines.
const TIMEZONE_COUNTRY: Record<string, string> = {
  "Africa/Abidjan": "CI", "Africa/Accra": "GH", "Africa/Addis_Ababa": "ET",
  "Africa/Algiers": "DZ", "Africa/Asmara": "ER", "Africa/Bamako": "ML",
  "Africa/Bangui": "CF", "Africa/Banjul": "GM", "Africa/Bissau": "GW",
  "Africa/Blantyre": "MW", "Africa/Brazzaville": "CG", "Africa/Bujumbura": "BI",
  "Africa/Cairo": "EG", "Africa/Casablanca": "MA", "Africa/Ceuta": "ES",
  "Africa/Conakry": "GN", "Africa/Dakar": "SN", "Africa/Dar_es_Salaam": "TZ",
  "Africa/Djibouti": "DJ", "Africa/Douala": "CM", "Africa/El_Aaiun": "EH",
  "Africa/Freetown": "SL", "Africa/Gaborone": "BW", "Africa/Harare": "ZW",
  "Africa/Johannesburg": "ZA", "Africa/Juba": "SS", "Africa/Kampala": "UG",
  "Africa/Khartoum": "SD", "Africa/Kigali": "RW", "Africa/Kinshasa": "CD",
  "Africa/Lagos": "NG", "Africa/Libreville": "GA", "Africa/Lome": "TG",
  "Africa/Luanda": "AO", "Africa/Lubumbashi": "CD", "Africa/Lusaka": "ZM",
  "Africa/Malabo": "GQ", "Africa/Maputo": "MZ", "Africa/Maseru": "LS",
  "Africa/Mbabane": "SZ", "Africa/Mogadishu": "SO", "Africa/Monrovia": "LR",
  "Africa/Nairobi": "KE", "Africa/Ndjamena": "TD", "Africa/Niamey": "NE",
  "Africa/Nouakchott": "MR", "Africa/Ouagadougou": "BF", "Africa/Porto-Novo": "BJ",
  "Africa/Sao_Tome": "ST", "Africa/Tripoli": "LY", "Africa/Tunis": "TN",
  "Africa/Windhoek": "NA",
  "America/Adak": "US", "America/Anchorage": "US", "America/Anguilla": "AI",
  "America/Antigua": "AG", "America/Araguaina": "BR",
  "America/Argentina/Buenos_Aires": "AR", "America/Argentina/Catamarca": "AR",
  "America/Argentina/Cordoba": "AR", "America/Argentina/Jujuy": "AR",
  "America/Argentina/La_Rioja": "AR", "America/Argentina/Mendoza": "AR",
  "America/Argentina/Rio_Gallegos": "AR", "America/Argentina/Salta": "AR",
  "America/Argentina/San_Juan": "AR", "America/Argentina/San_Luis": "AR",
  "America/Argentina/Tucuman": "AR", "America/Argentina/Ushuaia": "AR",
  "America/Aruba": "AW", "America/Asuncion": "PY", "America/Atikokan": "CA",
  "America/Bahia": "BR", "America/Bahia_Banderas": "MX", "America/Barbados": "BB",
  "America/Belem": "BR", "America/Belize": "BZ", "America/Blanc-Sablon": "CA",
  "America/Boa_Vista": "BR", "America/Bogota": "CO", "America/Boise": "US",
  "America/Cambridge_Bay": "CA", "America/Campo_Grande": "BR", "America/Cancun": "MX",
  "America/Caracas": "VE", "America/Cayenne": "GF", "America/Cayman": "KY",
  "America/Chicago": "US", "America/Chihuahua": "MX", "America/Costa_Rica": "CR",
  "America/Creston": "CA", "America/Cuiaba": "BR", "America/Curacao": "CW",
  "America/Danmarkshavn": "GL", "America/Dawson": "CA", "America/Dawson_Creek": "CA",
  "America/Denver": "US", "America/Detroit": "US", "America/Dominica": "DM",
  "America/Edmonton": "CA", "America/Eirunepe": "BR", "America/El_Salvador": "SV",
  "America/Fortaleza": "BR", "America/Glace_Bay": "CA", "America/Godthab": "GL",
  "America/Goose_Bay": "CA", "America/Grand_Turk": "TC", "America/Grenada": "GD",
  "America/Guadeloupe": "GP", "America/Guatemala": "GT", "America/Guayaquil": "EC",
  "America/Guyana": "GY", "America/Halifax": "CA", "America/Havana": "CU",
  "America/Hermosillo": "MX", "America/Indiana/Indianapolis": "US",
  "America/Indiana/Knox": "US", "America/Indiana/Marengo": "US",
  "America/Indiana/Petersburg": "US", "America/Indiana/Tell_City": "US",
  "America/Indiana/Vevay": "US", "America/Indiana/Vincennes": "US",
  "America/Indiana/Winamac": "US", "America/Inuvik": "CA", "America/Iqaluit": "CA",
  "America/Jamaica": "JM", "America/Juneau": "US",
  "America/Kentucky/Louisville": "US", "America/Kentucky/Monticello": "US",
  "America/Kralendijk": "BQ", "America/La_Paz": "BO", "America/Lima": "PE",
  "America/Los_Angeles": "US", "America/Lower_Princes": "SX", "America/Maceio": "BR",
  "America/Managua": "NI", "America/Manaus": "BR", "America/Marigot": "MF",
  "America/Martinique": "MQ", "America/Matamoros": "MX", "America/Mazatlan": "MX",
  "America/Menominee": "US", "America/Merida": "MX", "America/Metlakatla": "US",
  "America/Mexico_City": "MX", "America/Miquelon": "PM", "America/Moncton": "CA",
  "America/Monterrey": "MX", "America/Montevideo": "UY", "America/Montserrat": "MS",
  "America/Nassau": "BS", "America/New_York": "US", "America/Nipigon": "CA",
  "America/Nome": "US", "America/Noronha": "BR",
  "America/North_Dakota/Beulah": "US", "America/North_Dakota/Center": "US",
  "America/North_Dakota/New_Salem": "US", "America/Nuuk": "GL",
  "America/Ojinaga": "MX", "America/Panama": "PA", "America/Pangnirtung": "CA",
  "America/Paramaribo": "SR", "America/Phoenix": "US", "America/Port-au-Prince": "HT",
  "America/Port_of_Spain": "TT", "America/Porto_Velho": "BR",
  "America/Puerto_Rico": "PR", "America/Punta_Arenas": "CL",
  "America/Rainy_River": "CA", "America/Rankin_Inlet": "CA", "America/Recife": "BR",
  "America/Regina": "CA", "America/Resolute": "CA", "America/Rio_Branco": "BR",
  "America/Santarem": "BR", "America/Santiago": "CL", "America/Santo_Domingo": "DO",
  "America/Sao_Paulo": "BR", "America/Scoresbysund": "GL", "America/Sitka": "US",
  "America/St_Barthelemy": "BL", "America/St_Johns": "CA", "America/St_Kitts": "KN",
  "America/St_Lucia": "LC", "America/St_Thomas": "VI", "America/St_Vincent": "VC",
  "America/Swift_Current": "CA", "America/Tegucigalpa": "HN", "America/Thule": "GL",
  "America/Thunder_Bay": "CA", "America/Tijuana": "MX", "America/Toronto": "CA",
  "America/Tortola": "VG", "America/Vancouver": "CA", "America/Whitehorse": "CA",
  "America/Winnipeg": "CA", "America/Yakutat": "US", "America/Yellowknife": "CA",
  "Asia/Aden": "YE", "Asia/Almaty": "KZ", "Asia/Amman": "JO", "Asia/Anadyr": "RU",
  "Asia/Aqtau": "KZ", "Asia/Aqtobe": "KZ", "Asia/Ashgabat": "TM",
  "Asia/Atyrau": "KZ", "Asia/Baghdad": "IQ", "Asia/Bahrain": "BH",
  "Asia/Baku": "AZ", "Asia/Bangkok": "TH", "Asia/Barnaul": "RU",
  "Asia/Beirut": "LB", "Asia/Bishkek": "KG", "Asia/Brunei": "BN",
  "Asia/Chita": "RU", "Asia/Choibalsan": "MN", "Asia/Colombo": "LK",
  "Asia/Damascus": "SY", "Asia/Dhaka": "BD", "Asia/Dili": "TL",
  "Asia/Dubai": "AE", "Asia/Dushanbe": "TJ", "Asia/Famagusta": "CY",
  "Asia/Gaza": "PS", "Asia/Hebron": "PS", "Asia/Ho_Chi_Minh": "VN",
  "Asia/Hong_Kong": "HK", "Asia/Hovd": "MN", "Asia/Irkutsk": "RU",
  "Asia/Jakarta": "ID", "Asia/Jayapura": "ID", "Asia/Jerusalem": "IL",
  "Asia/Kabul": "AF", "Asia/Kamchatka": "RU", "Asia/Karachi": "PK",
  "Asia/Kathmandu": "NP", "Asia/Khandyga": "RU", "Asia/Kolkata": "IN",
  "Asia/Krasnoyarsk": "RU", "Asia/Kuala_Lumpur": "MY", "Asia/Kuching": "MY",
  "Asia/Kuwait": "KW", "Asia/Macau": "MO", "Asia/Magadan": "RU",
  "Asia/Makassar": "ID", "Asia/Manila": "PH", "Asia/Muscat": "OM",
  "Asia/Nicosia": "CY", "Asia/Novokuznetsk": "RU", "Asia/Novosibirsk": "RU",
  "Asia/Omsk": "RU", "Asia/Oral": "KZ", "Asia/Phnom_Penh": "KH",
  "Asia/Pontianak": "ID", "Asia/Pyongyang": "KP", "Asia/Qatar": "QA",
  "Asia/Qostanay": "KZ", "Asia/Qyzylorda": "KZ", "Asia/Riyadh": "SA",
  "Asia/Sakhalin": "RU", "Asia/Samarkand": "UZ", "Asia/Seoul": "KR",
  "Asia/Shanghai": "CN", "Asia/Singapore": "SG", "Asia/Srednekolymsk": "RU",
  "Asia/Taipei": "TW", "Asia/Tashkent": "UZ", "Asia/Tbilisi": "GE",
  "Asia/Tehran": "IR", "Asia/Thimphu": "BT", "Asia/Tokyo": "JP",
  "Asia/Tomsk": "RU", "Asia/Ulaanbaatar": "MN", "Asia/Urumqi": "CN",
  "Asia/Ust-Nera": "RU", "Asia/Vientiane": "LA", "Asia/Vladivostok": "RU",
  "Asia/Yakutsk": "RU", "Asia/Yangon": "MM", "Asia/Yekaterinburg": "RU",
  "Asia/Yerevan": "AM",
  "Atlantic/Azores": "PT", "Atlantic/Bermuda": "BM", "Atlantic/Canary": "ES",
  "Atlantic/Cape_Verde": "CV", "Atlantic/Faroe": "FO", "Atlantic/Madeira": "PT",
  "Atlantic/Reykjavik": "IS", "Atlantic/South_Georgia": "GS",
  "Atlantic/St_Helena": "SH", "Atlantic/Stanley": "FK",
  "Australia/Adelaide": "AU", "Australia/Brisbane": "AU", "Australia/Broken_Hill": "AU",
  "Australia/Darwin": "AU", "Australia/Eucla": "AU", "Australia/Hobart": "AU",
  "Australia/Lindeman": "AU", "Australia/Lord_Howe": "AU", "Australia/Melbourne": "AU",
  "Australia/Perth": "AU", "Australia/Sydney": "AU",
  "Europe/Amsterdam": "NL", "Europe/Andorra": "AD", "Europe/Astrakhan": "RU",
  "Europe/Athens": "GR", "Europe/Belgrade": "RS", "Europe/Berlin": "DE",
  "Europe/Bratislava": "SK", "Europe/Brussels": "BE", "Europe/Bucharest": "RO",
  "Europe/Budapest": "HU", "Europe/Busingen": "DE", "Europe/Chisinau": "MD",
  "Europe/Copenhagen": "DK", "Europe/Dublin": "IE", "Europe/Gibraltar": "GI",
  "Europe/Guernsey": "GG", "Europe/Helsinki": "FI", "Europe/Isle_of_Man": "IM",
  "Europe/Istanbul": "TR", "Europe/Jersey": "JE", "Europe/Kaliningrad": "RU",
  "Europe/Kiev": "UA", "Europe/Kirov": "RU", "Europe/Lisbon": "PT",
  "Europe/Ljubljana": "SI", "Europe/London": "GB", "Europe/Luxembourg": "LU",
  "Europe/Madrid": "ES", "Europe/Malta": "MT", "Europe/Mariehamn": "AX",
  "Europe/Minsk": "BY", "Europe/Monaco": "MC", "Europe/Moscow": "RU",
  "Europe/Nicosia": "CY", "Europe/Oslo": "NO", "Europe/Paris": "FR",
  "Europe/Podgorica": "ME", "Europe/Prague": "CZ", "Europe/Riga": "LV",
  "Europe/Rome": "IT", "Europe/Samara": "RU", "Europe/San_Marino": "SM",
  "Europe/Sarajevo": "BA", "Europe/Saratov": "RU", "Europe/Simferopol": "UA",
  "Europe/Skopje": "MK", "Europe/Sofia": "BG", "Europe/Stockholm": "SE",
  "Europe/Tallinn": "EE", "Europe/Tirane": "AL", "Europe/Ulyanovsk": "RU",
  "Europe/Uzhgorod": "UA", "Europe/Vaduz": "LI", "Europe/Vatican": "VA",
  "Europe/Vienna": "AT", "Europe/Vilnius": "LT", "Europe/Volgograd": "RU",
  "Europe/Warsaw": "PL", "Europe/Zagreb": "HR", "Europe/Zaporozhye": "UA",
  "Europe/Zurich": "CH",
  "Indian/Antananarivo": "MG", "Indian/Chagos": "IO", "Indian/Christmas": "CX",
  "Indian/Cocos": "CC", "Indian/Comoro": "KM", "Indian/Kerguelen": "TF",
  "Indian/Mahe": "SC", "Indian/Maldives": "MV", "Indian/Mauritius": "MU",
  "Indian/Mayotte": "YT", "Indian/Reunion": "RE",
  "Pacific/Apia": "WS", "Pacific/Auckland": "NZ", "Pacific/Bougainville": "PG",
  "Pacific/Chatham": "NZ", "Pacific/Chuuk": "FM", "Pacific/Easter": "CL",
  "Pacific/Efate": "VU", "Pacific/Enderbury": "KI", "Pacific/Fakaofo": "TK",
  "Pacific/Fiji": "FJ", "Pacific/Funafuti": "TV", "Pacific/Galapagos": "EC",
  "Pacific/Gambier": "PF", "Pacific/Guadalcanal": "SB", "Pacific/Guam": "GU",
  "Pacific/Honolulu": "US", "Pacific/Kiritimati": "KI", "Pacific/Kosrae": "FM",
  "Pacific/Kwajalein": "MH", "Pacific/Majuro": "MH", "Pacific/Marquesas": "PF",
  "Pacific/Midway": "UM", "Pacific/Nauru": "NR", "Pacific/Niue": "NU",
  "Pacific/Norfolk": "NF", "Pacific/Noumea": "NC", "Pacific/Pago_Pago": "AS",
  "Pacific/Palau": "PW", "Pacific/Pitcairn": "PN", "Pacific/Pohnpei": "FM",
  "Pacific/Port_Moresby": "PG", "Pacific/Rarotonga": "CK", "Pacific/Saipan": "MP",
  "Pacific/Tahiti": "PF", "Pacific/Tarawa": "KI", "Pacific/Tongatapu": "TO",
  "Pacific/Wake": "UM", "Pacific/Wallis": "WF",
};

const detectCountry = (list: any[]) => {
  if (!list?.length) return undefined;

  // PRIMARY: static timezone → ISO country code map (works in all browsers)
  try {
    const rawTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tz = TIMEZONE_IANA_ALIASES[rawTz] ?? rawTz;
    const countryCode = TIMEZONE_COUNTRY[tz];
    if (countryCode) {
      const countryName = new Intl.DisplayNames(["en"], { type: "region" }).of(countryCode);
      if (countryName) {
        const found = matchCountryName(list, countryName);
        if (found) return found;
      }
    }
  } catch {}

  // SECONDARY: Intl.Locale.getTimeZones() — modern browsers only
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const regions: string[] = (Intl as any).supportedValuesOf?.("region") ?? [];
    const displayNames = new Intl.DisplayNames(["en"], { type: "region" });
    for (const region of regions) {
      try {
        const locale = new Intl.Locale(`en-${region}`);
        const tzs: string[] | undefined = (locale as any).getTimeZones?.();
        if (Array.isArray(tzs) && tzs.includes(tz)) {
          const countryName = displayNames.of(region);
          if (countryName) {
            const found = matchCountryName(list, countryName);
            if (found) return found;
          }
        }
      } catch {}
    }
  } catch {}

  // FALLBACK: browser language region code (e.g. "en-US" → United States)
  try {
    const regionCode = navigator.language.split(/[-_]/)[1];
    if (regionCode) {
      const countryName = new Intl.DisplayNames(["en"], { type: "region" }).of(regionCode);
      if (countryName) {
        const found = matchCountryName(list, countryName);
        if (found) return found;
      }
    }
  } catch {}

  return undefined;
};

const detectLanguage = (list: any[]) => {
  if (!list?.length) return undefined;
  try {
    const langCode = navigator.language.split(/[-_]/)[0].toLowerCase();
    // Direct code match (most reliable)
    const byCode = list.find((l) => l.code?.toLowerCase() === langCode);
    if (byCode) return byCode;
    // English name match as fallback
    const langNameEn = new Intl.DisplayNames(["en"], { type: "language" }).of(langCode);
    if (langNameEn) {
      const byName = list.find(
        (l) => l.name?.toLowerCase() === langNameEn.toLowerCase()
      );
      if (byName) return byName;
    }
  } catch {}
  // Default to English if the browser language isn't in the API's language list
  return list.find((l) => l.name?.toLowerCase() === "english") ?? list[0];
};

// Deprecated IANA names some browsers still report → canonical names used by the API.
const TIMEZONE_IANA_ALIASES: Record<string, string> = {
  "Asia/Calcutta":    "Asia/Kolkata",
  "Asia/Rangoon":     "Asia/Yangon",
  "Asia/Katmandu":    "Asia/Kathmandu",
  "Asia/Ulaanbaatar": "Asia/Ulaanbaatar",
  "America/Godthab":  "America/Nuuk",
  "Pacific/Truk":     "Pacific/Chuuk",
};

const detectTimezone = (list: any[]) => {
  if (!list?.length) return undefined;

  try {
    const rawTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const browserTz = TIMEZONE_IANA_ALIASES[rawTz] ?? rawTz;
    // zone_name uses IANA underscored format (e.g. "America/New_York"); name may use spaces
    const exact = list.find(
      (tz) => tz.zone_name === browserTz || tz.name === browserTz
    );
    if (exact) return exact;

    // UTC offset fallback — handles cases where the exact IANA name isn't in the list
    const offsetMinutes = -new Date().getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absH = Math.floor(Math.abs(offsetMinutes) / 60);
    const absM = Math.abs(offsetMinutes) % 60;
    // Build two formats to cover "UTC+3:00" and "UTC+03:00" style names
    const offsetShort = `UTC${sign}${absH}:${String(absM).padStart(2, "0")}`;
    const offsetPadded = `UTC${sign}${String(absH).padStart(2, "0")}:${String(absM).padStart(2, "0")}`;
    const byOffset = list.find((tz) => {
      const n = tz.name?.toUpperCase() ?? "";
      return n.includes(offsetShort.toUpperCase()) || n.includes(offsetPadded.toUpperCase());
    });
    if (byOffset) return byOffset;
  } catch {}

  return list.find((tz) => tz.zone_name === "UTC" || tz.name === "UTC") ?? list[0];
};

const PasswordHint = ({ met, started, children }: { met: boolean; started: boolean; children: React.ReactNode }) => {
  const [visible, setVisible] = useState(true);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (!met) {
      setVisible(true);
      setExiting(false);
    }
  }, [met]);

  useEffect(() => {
    if (met && started) {
      const fadeTimer = setTimeout(() => setExiting(true), 700);
      const hideTimer = setTimeout(() => setVisible(false), 1200);
      return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer); };
    }
  }, [met, started]);

  if (!visible) return null;

  const color = !started ? "#9e9e9e" : met ? "#66bb6a" : "#d32f2f";

  return (
    <Typography
      variant="subtitle2"
      sx={{
        color,
        opacity: exiting ? 0 : 1,
        transform: exiting ? "translateX(12px)" : "translateX(0)",
        transition: exiting
          ? "opacity 0.45s ease, transform 0.45s ease, color 0.2s"
          : "color 0.2s ease",
      }}
    >
      {children}
    </Typography>
  );
};

const deleteMczProfileTypeCookie = (path = "/") => {
  const hostname = window.location.hostname;
  const domain = hostname.replace("mmaz", "");

  const cookieString = `${
    process.env.REACT_APP_MCZ_PROFILE_TYPE_COOKIE
  }=; Expires=Thu, 01 Jan 1970 00:00:00 UTC; Path=${path};${
    domain ? ` Domain=${domain};` : ""
  }`;
  document.cookie = cookieString;
  document.cookie = cookieString + " Secure;";
  document.cookie = cookieString + " SameSite=None;";
};

const SignUp = () => {
  const emailForSignup = localStorage.getItem("emailForSignup");
  const history = useHistory();
  const [email, setEmail] = useState(emailForSignup || "");
  const [emailError, setEmailError] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [country, setCountry] = useState<Country | undefined>(undefined);
  const [timezone, setTimezone] = useState<any>(undefined);
  const [language, setLanguage] = useState<Language | undefined>(undefined);
  const [termsOfUse, setTermsOfUse] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const { languages, setLanguages } = useContext(LanguagesContext);
  const { timezones, setTimezones } = useContext(TimezonesContext);
  const { countries, setCountries } = useContext(CountriesContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [signupError, setSignupError] = useState("");
  const [passwordIsBeingEdited, setPasswordIsBeingEdited] =
    React.useState(false);
  const [passwordHasAtLeast11Characters, setPasswordHasAtLeast11Characters] =
    React.useState(false);
  const [passwordsMatch, setPasswordsMatch] = React.useState(false);
  const [passwordHasAtLeastOneDigit, setPasswordHasAtLeastOneDigit] =
    React.useState(false);
  const [
    passwordHasAtLeastOneUppercaseLetter,
    setPasswordHasAtLeastOneUppercaseLetter,
  ] = React.useState(false);
  const [
    passwordHasAtLeastOneLowercaseLetter,
    setPasswordHasAtLeastOneLowercaseLetter,
  ] = React.useState(false);
  const [
    passwordHasAtLeastOneSpecialCharacter,
    setPasswordHasAtLeastOneSpecialCharacter,
  ] = React.useState(false);
  const {
    profileNameLogin,
    setProfileNameLogin,
    profileImageURLLogin,
    setProfileImageURLLogin,
    profileUrlName,
    setProfileUrlName,
    showSignup,
    setShowSignup,
  } = useContext(AuthContext);
  const [hasFetchedProfileInfo, setHasFetchedProfileInfo] = useState(false);

  if (!showSignup && hasFetchedProfileInfo) {
    history.push("/");
  }

  const { translations } = useContext(TranslationsContext);

  const { setInformation } = useContext(InformationContext);

  const [recaptchaApproved, setRecaptchaApproved] = useState(false);
  function onChangeRecaptcha(value) {
    if (value) {
      setRecaptchaApproved(true);
    } else {
      setRecaptchaApproved(false);
    }
  }
  const location = useLocation();

  const getAppAndProfileFromUrl = () => {
    const { pathname } = location;
    console.log("🚀 ~ getAppAndProfileFromUrl ~ pathname:", pathname);
    const parts = pathname.split("/").filter((part) => part);
    console.log("🚀 ~ getAppAndProfileFromUrl ~ parts:", parts);

    let appFromUrl, profileFromUrl, labelValueFromUrl;

    if (parts.length >= 2) {
      appFromUrl = parts[0];
      profileFromUrl = parts[2];
      labelValueFromUrl = parts[3];
    }
    return { appFromUrl, profileFromUrl, labelValueFromUrl };
  };

  const currentUrl = window.location.href;
  const url = new URL(currentUrl);

  const baseUrlMmaz = `${url.protocol}//${url.host}`;

  const domainUrl = baseUrlMmaz.replace("mmaz", "mmdz");

  const buttonSX = {
    backgroundColor: "#3399FE",
    borderRadius: "8px",
    fontWeight: 600,
    letterSpacing: "0.4px",
    padding: "10px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#1a77d4",
      boxShadow: "0 6px 18px rgba(51,153,254,0.4)",
      transform: "translateY(-1px)",
    },
  };
  const { app, profile } = useParams();

  const urlParams = new URLSearchParams(window.location.search);
  const typeFromUrl = urlParams.get("type");

  if (typeFromUrl) {
    setCookie(process.env.REACT_APP_MCZ_PROFILE_TYPE_COOKIE, typeFromUrl, 365);
  } else {
    deleteMczProfileTypeCookie();
  }

  document.title = profileNameLogin ? `${profileNameLogin} - Sign Up` : "Sign Up";

  useEffect(() => {
    const { profileFromUrl, appFromUrl, labelValueFromUrl } =
      getAppAndProfileFromUrl();
    setCookie(process.env.REACT_APP_MMAZ_PROFILE_COOKIE, profile, 365);
    setCookie(process.env.REACT_APP_MCZ_PROFILE_COOKIE, profile, 365);
    setCookie(process.env.REACT_APP_MMAZ_APPLICATION_COOKIE, appFromUrl, 365);
    const urlParams = new URLSearchParams(window.location.search);
    const labelValue = urlParams.get("type");
    if (labelValue) {
      setCookie("MCZ_PROFILE_TYPE", labelValue, 365);
    }

    setIsLoading(true);

    const profilePromise = (profileNameLogin && profileUrlName)
      ? Promise.resolve().then(() => setHasFetchedProfileInfo(true))
      : (() => {
          const tokenFromLocalStorage = localStorage.getItem("activationToken");
          let payload: any = {
            domain: window.location.hostname.split(".").slice(1).join("."),
            application: app,
            profile: profile,
          };
          if (tokenFromLocalStorage) {
            payload = { ...payload, token: tokenFromLocalStorage };
          }
          return API.post(ApiUri.PROFILE + ApiUri.INFORMATION, payload).then(
            (response) => {
              const data = response.data.data;
              if (data?.invitation?.email) {
                localStorage.setItem("emailForSignup", data.invitation.email);
                setEmail(data.invitation.email);
              }
              setCookie(process.env.REACT_APP_WHITE_LABEL_COOKIE, data.white_label, 365);
              setInformation(data);
              setCookie("APP_NAME", data.app_name, 365);
              setCookie("WEB", data.web, 365);
              setCookie("DOMAIN_URL", data.domain, 365);
              if (profileFromUrl !== data.url_name) {
                history.replace(`/${appFromUrl ?? "mmdz"}/${data.url_name}/`);
              }
              setProfileImageURLLogin(data.logo_url ?? "");
              setShowSignup(data.is_registration_enabled !== false);
              setProfileNameLogin(data.profile_name ?? data.profile_information?.name);
              setProfileUrlName(data.url_name);
              setHasFetchedProfileInfo(true);
            },
            () => { setHasFetchedProfileInfo(true); }
          );
        })();

    const dataPromise = Promise.all([
      languages.length === 0
        ? API.get(ApiUri.DATA + ApiUri.LANGUAGE).then(r => { const d = r.data.data; setLanguages(d); setLanguage(detectLanguage(d)); }).catch(() => {})
        : (() => { setLanguage(detectLanguage(languages)); })(),
      countries.length === 0
        ? API.get(ApiUri.DATA + ApiUri.COUNTRY).then(r => { const d = r.data.data; setCountries(d); setCountry(detectCountry(d)); }).catch(() => {})
        : (() => { setCountry(detectCountry(countries)); })(),
      timezones.length === 0
        ? API.get(ApiUri.DATA + ApiUri.TIMEZONE).then(r => {
            const data = r.data.data;
            setTimezones(data);
            setTimezone(detectTimezone(data));
          }).catch(() => {})
        : (() => { setTimezone(detectTimezone(timezones)); })(),
    ]);

    Promise.all([profilePromise, dataPromise]).finally(() => {
      setIsLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRegisterButtonPress = (event) => {
    setSignupError("");
    const payload = {
      first_name: firstName,
      last_name: lastName,
      email: email,
      password: password,
      repeat_password: repeatPassword,
      country: country?.id,
      timezone: timezone?.id,
      language: language?.id,
      // if the user clicks in the switch, use 1, otherwise 0
      terms_of_use_and_privacy_policy: 1,
      // optional
      newsletter_opt_in: 1,
    };
    const profileCookie = getCookie(process.env.REACT_APP_MMAZ_PROFILE_COOKIE);
    const appCookie = getCookie(process.env.REACT_APP_MMAZ_APPLICATION_COOKIE);
    setIsRegistering(true);
    API.post(
      ApiUri.AUTH + ApiUri.SIGNUP + "/" + appCookie + "/" + profileCookie,
      payload,
      { timeout: 60000 }
    ).then(
      (response) => {
        localStorage.removeItem("emailForSignup");
        setIsRegistering(false);
        history.push(`/${app}/${profile}`, {
          successMessage:
            response.data.data?.message ??
            translations.account_not_active_by_url ??
            "Please check your inbox and activate the account using the sent url.",
        });
      },
      (error) => {
        if (error.code === "ECONNABORTED") {
          setSignupError(
            "Registration is taking longer than expected. Your account may have been created — please check your email before trying again."
          );
        } else {
          setSignupError(
            error.response?.data?.data?.errors[0]?.message ??
            error.response?.data?.message ??
            "Registration failed. Please try again."
          );
        }
        setIsRegistering(false);
      }
    );
  };

  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;

    if (name === "country") {
      const selectedCountry = countries?.find(
        (country) => country.id === value
      );
      setCountry(selectedCountry);
    } else if (name === "timezone") {
      const selectedTimezone = timezones.find(
        (timezone) => timezone.id === value
      );
      setTimezone(selectedTimezone);
    } else if (name === "language") {
      const selectedLanguage = languages?.find(
        (language) => language.id === value
      );
      setLanguage(selectedLanguage);
    }
  };

  const handleEditPassword = (event) => {
    let { name, value } = event.target;
    value = value.trim();
    if (signupError) setSignupError("");
    if (!passwordIsBeingEdited) {
      setPasswordIsBeingEdited(true);
    }
    if (name === "password") {
      setPasswordsMatch(value.length > 0 && value === repeatPassword);
      setPasswordHasAtLeast11Characters(value.length >= 11);
      setPasswordHasAtLeastOneDigit(/\d/.test(value));
      setPasswordHasAtLeastOneUppercaseLetter(/[A-Z]/.test(value));
      setPasswordHasAtLeastOneLowercaseLetter(/[a-z]/.test(value));
      setPasswordHasAtLeastOneSpecialCharacter(
        /[!@#$%^&*(),.?":{}|<>]/.test(value)
      );
      setPassword(value);
    } else if (name === "repeatPassword") {
      setPasswordsMatch(value.length > 0 && value === password);
      setRepeatPassword(value);
    }
  };

  const passwordsAreInvalid = passwordIsBeingEdited
    ? !passwordHasAtLeast11Characters ||
      !passwordHasAtLeastOneDigit ||
      !passwordsMatch ||
      !passwordHasAtLeastOneLowercaseLetter ||
      !passwordHasAtLeastOneUppercaseLetter ||
      !passwordHasAtLeastOneSpecialCharacter
    : true;

  const handleBlur = () => {
    setEmailError(!isValidEmail(email));
  };

  const emailIsValid = isValidEmail(email);

  const handleChangeEmail = (event) => {
    const input = event.target.value.trim();
    setEmail(input);
    if (signupError) setSignupError("");
    if (emailError) {
      setEmailError(!isValidEmail(input));
    }
  };

  const {
    minimumPasswordLengthText,
    passwordsMatchingText,
    atLeastOneDigitText,
    atLeastOneUppercaseText,
    atLeastOneLowercaseText,
    atLeastOneSpecialText,
  } = getPasswordValidationTexts(translations);

  const headerContent = hasFetchedProfileInfo ? (
    <Stack alignItems="center" spacing={1.5}>
      <img
        src={profileImageURLLogin}
        alt={profileNameLogin}
        style={{
          width: "68px",
          height: "68px",
          borderRadius: "50%",
          border: "2px solid rgba(51,153,254,0.55)",
          boxShadow: "0 0 0 5px rgba(51,153,254,0.12), 0 8px 24px rgba(0,0,0,0.4)",
        }}
      />
      <Box>
        <Typography
          align="center"
          variant="h6"
          sx={{
            wordBreak: "break-word",
            fontWeight: 700,
            color: "#ffffff",
            letterSpacing: "-0.2px",
            lineHeight: 1.2,
          }}
        >
          {profileNameLogin}
        </Typography>
        <Typography
          align="center"
          sx={{
            color: "rgba(255,255,255,0.45)",
            fontSize: "10px",
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            mt: 0.5,
          }}
        >
          Create Account
        </Typography>
      </Box>
    </Stack>
  ) : null;

  return (
    <BaseComponentLogin backButton headerSection={headerContent} isLoading={isLoading}>
      {hasFetchedProfileInfo ? (
        <>
          <Stack spacing={2} width={"100%"}>
            <TextField
              fullWidth
              size="small"
              label={translations["label.my_first_name"] || "My First Name"}
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label={translations["label.my_last_name"] || "My Last Name"}
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label={translations["label.my_email"] || "My Email"}
              value={email}
              onChange={handleChangeEmail}
              onBlur={handleBlur}
              error={emailError}
              inputProps={{ autoCapitalize: "none", autoComplete: "off" }}
              disabled={
                typeof emailForSignup === "string" && emailForSignup.length > 0
              }
              // helperText={emailError ? "Invalid email" : ""}
            />
            <TextField
              fullWidth
              size="small"
              label={translations["label.password"] || "Password"}
              name="password"
              value={password}
              onChange={handleEditPassword}
              type="password"
              autoComplete="new-password"
            />
            <TextField
              fullWidth
              size="small"
              label={translations["label.repeat_password"] || "Repeat Password"}
              name="repeatPassword"
              value={repeatPassword}
              onChange={handleEditPassword}
              type="password"
              autoComplete="new-password"
            />
            <Stack spacing={0.5}>
              <PasswordHint met={passwordHasAtLeast11Characters} started={passwordIsBeingEdited}>
                {minimumPasswordLengthText}
              </PasswordHint>
              <PasswordHint met={passwordHasAtLeastOneDigit} started={passwordIsBeingEdited}>
                {atLeastOneDigitText}
              </PasswordHint>
              <PasswordHint met={passwordHasAtLeastOneUppercaseLetter} started={passwordIsBeingEdited}>
                {atLeastOneUppercaseText}
              </PasswordHint>
              <PasswordHint met={passwordHasAtLeastOneLowercaseLetter} started={passwordIsBeingEdited}>
                {atLeastOneLowercaseText}
              </PasswordHint>
              <PasswordHint met={passwordHasAtLeastOneSpecialCharacter} started={passwordIsBeingEdited}>
                {atLeastOneSpecialText}
              </PasswordHint>
              <PasswordHint met={passwordsMatch} started={passwordIsBeingEdited}>
                {passwordsMatchingText}
              </PasswordHint>
            </Stack>

            <FormControl fullWidth size="small" required>
              <InputLabel>
                {translations["label.country"] || "Country"}
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label={translations["label.country"] || "Country"}
                name="country"
                value={country?.id ?? ""}
                onChange={handleSelectChange}
              >
                {countries?.map((country) => (
                  <MenuItem value={country?.id} key={country?.id}>
                    {country?.country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" required>
              <InputLabel>
                {translations["label.timezone"] || "Timezone"}
              </InputLabel>
              <Select
                label={translations["label.timezone"] || "Timezone"}
                name="timezone"
                value={timezone?.id ?? ""}
                onChange={handleSelectChange}
              >
                {timezones?.map((tz) => (
                  <MenuItem value={tz?.id} key={tz?.id}>
                    {tz?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>
                {translations["label.language"] || "Language"}
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="language"
                value={language?.id ?? ""}
                onChange={handleSelectChange}
                label={translations["label.language"] || "Language"}
              >
                {languages?.map((language) => (
                  <MenuItem value={language?.id} key={language?.id}>
                    {language?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <InputLabel sx={{ marginTop: 2 }}>
              {translations["label.registration_agreement"] ||
                "By registering you agree to the:"}
            </InputLabel>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={termsOfUse}
                    onChange={() => setTermsOfUse(!termsOfUse)}
                    name="termsOfUse"
                    // color="primary"
                    // color="success"
                  />
                }
                label={
                  <Typography>
                    <a
                      href={`${domainUrl}/${profileUrlName}/public/terms-of-use`}
                      style={{ color: "#3399FE" }}
                      target="_blank"
                    >
                      {translations["label.terms_of_use"] || "Terms of Use"}
                    </a>{" "}
                    &{" "}
                    <a
                      href={`${domainUrl}/${profileUrlName}/public/privacy-policy`}
                      style={{ color: "#3399FE" }}
                      target="_blank"
                    >
                      {translations["label.privacy_policy"] || "Privacy Policy"}
                    </a>
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={marketing}
                    onChange={() => setMarketing(!marketing)}
                    name="marketing"
                    // color="primary"
                    // color="success"
                  />
                }
                label={
                  <Typography>
                    <a
                      href={`${domainUrl}/${profileUrlName}/public/marketing`}
                      style={{ color: "#3399FE" }}
                      target="_blank"
                    >
                      {translations["label.marketing_permissions"] ||
                        "Marketing Permissions"}
                    </a>
                  </Typography>
                }
              />
            </FormGroup>
          </Stack>

          <Box sx={{ mt: 2 }}>
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_SITEKEY_RECAPTCHA}
              onChange={onChangeRecaptcha}
            />
          </Box>

          <Stack
            alignItems="center"
            width={"100%"}
            marginTop={3}
            spacing={2}
            paddingBottom={2}
          >
            {signupError && (
              <Box
                sx={{
                  width: "100%",
                  px: 1.5,
                  py: 1,
                  borderRadius: "8px",
                  backgroundColor: "rgba(211,47,47,0.08)",
                  border: "1px solid rgba(211,47,47,0.25)",
                }}
              >
                <Typography
                  fontSize={13}
                  sx={{ color: "#c62828", textAlign: "center" }}
                >
                  {signupError}
                </Typography>
              </Box>
            )}

            <ActionButtonSmall
              sx={{
                ...buttonSX,
                "&.Mui-disabled": {
                  backgroundColor: "rgba(51,153,254,0.28)",
                  color: "rgba(255,255,255,0.55)",
                },
              }}
              onClick={handleRegisterButtonPress}
              disabled={
                isRegistering ||
                email.length === 0 ||
                !emailIsValid ||
                firstName.length === 0 ||
                lastName.length === 0 ||
                !country ||
                !timezone ||
                !language ||
                !termsOfUse ||
                passwordsAreInvalid ||
                !recaptchaApproved
              }
              fullWidth
              ignoreStyles={true}
            >
              {isRegistering ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "rgba(255,255,255,0.7)" }} />
                  <span>Creating your account...</span>
                </Box>
              ) : (
                translations["button.register"] || "Register"
              )}
            </ActionButtonSmall>
          </Stack>

          <Stack
            spacing={1}
            mt={"auto"}
            alignItems={"center"}
            justifyContent={"center"}
            paddingTop={2}
          >
            <Typography fontSize={14}>
              {translations["label.existing_account"] ||
                "Already have an account?"}{" "}
              <Link to="/" className="login-link">
                {translations["button.log_in"] || "Log in"}
              </Link>
            </Typography>
          </Stack>
        </>
      ) : null}
    </BaseComponentLogin>
  );
};

export default SignUp;
